import { useEffect } from 'react';

import { logError } from '@/utils/errorLogger';

/**
 * Custom hook to handle print styles loading and media query detection
 * Lazily loads print styles when printing is detected or requested
 */
export const usePrintStyles = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let printStylesLoaded = false;

    const loadPrintStyles = async (): Promise<void> => {
      if (printStylesLoaded) return;
      printStylesLoaded = true;

      try {
        await import('@/styles/print.css');
      } catch (error) {
        logError(error, {
          source: 'usePrintStyles:loadPrintStyles',
          context: { reason: 'print_css_lazy_import' },
          severity: 'warn',
        });
      }
    };

    const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null;

    const handleMediaChange = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        void loadPrintStyles();
      }
    };

    const handleBeforePrint = (): void => {
      void loadPrintStyles();
    };

    // Set up media query listener
    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleMediaChange);
      } else if (typeof mediaQuery.addListener === 'function') {
        // Fallback for older browsers
        mediaQuery.addListener(handleMediaChange);
      }
    }

    // Listen for beforeprint event
    window.addEventListener('beforeprint', handleBeforePrint);

    // Load print styles after initial page load to improve LCP
    const idleHandle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(() => void loadPrintStyles())
        : window.setTimeout(() => void loadPrintStyles(), 2000);

    // Cleanup function
    return () => {
      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === 'function') {
          mediaQuery.removeEventListener('change', handleMediaChange);
        } else if (typeof mediaQuery.removeListener === 'function') {
          // Fallback for older browsers
          mediaQuery.removeListener(handleMediaChange);
        }
      }

      window.removeEventListener('beforeprint', handleBeforePrint);

      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      } else {
        window.clearTimeout(idleHandle);
      }
    };
  }, []);
};
